import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
import { playStack, stopAll, isPlaying } from "@/lib/audioEngine";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

const moods = ["calm","focus","sleep","energize","uplift","anxiety_relief","clarity","confidence"];

export default function StackGenerator(){
  const [targetMood, setTargetMood] = useState("calm");
  const [duration, setDuration] = useState(20);
  const [intensity, setIntensity] = useState("medium");
  const [plan, setPlan] = useState(null);

  const generate = async () => {
    try {
      const res = await axios.post(`${API}/stacks/generate`, { target_mood: targetMood, duration_min: Number(duration), intensity, include_habits: true });
      setPlan(res.data);
      localStorage.setItem("lastPlan", JSON.stringify(res.data));
      toast.success("Stack generated");
    } catch(e){ console.error(e); toast.error("Failed to generate"); }
  }

  const play = () => {
    if(!plan) return;
    const calib = Number(localStorage.getItem("calibrationCents")||plan.pitch_offset_cents||0);
    stopAll();
    playStack({phases: plan.phases, calibrationCents: calib});
    toast.success("Playback started");
  };

  const stop = () => { stopAll(); };

  useEffect(()=>{ const saved = localStorage.getItem("lastPlan"); if(saved){ try{ setPlan(JSON.parse(saved)); }catch{} } },[]);

  return (
    <div className="mx-auto max-w-3xl px-6 py-8 space-y-6">
      <h1 className="text-3xl font-semibold tracking-tight" data-testid="stack-page-title">Build a 432Hz stack</h1>

      <Card className="sg-frame">
        <CardHeader><CardTitle>Preferences</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="mood">Target mood</Label>
              <Select value={targetMood} onValueChange={setTargetMood}>
                <SelectTrigger id="mood" data-testid="target-mood-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {moods.map(m => <SelectItem key={m} value={m} className="capitalize">{m.replace('_',' ')}</SelectItem>)}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="duration">Duration (min)</Label>
              <Input id="duration" type="number" min={5} max={120} value={duration} onChange={e=>setDuration(e.target.value)} data-testid="duration-input"/>
            </div>
            <div>
              <Label>Intensity</Label>
              <Select value={intensity} onValueChange={setIntensity}>
                <SelectTrigger data-testid="intensity-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex gap-3">
            <Button onClick={generate} data-testid="generate-stack-btn">Generate</Button>
            {plan && !isPlaying() && <Button variant="secondary" onClick={play} data-testid="play-stack-btn">Play</Button>}
            {isPlaying() && <Button variant="destructive" onClick={stop} data-testid="stop-stack-btn">Stop</Button>}
          </div>
        </CardContent>
      </Card>

      {plan && (
        <Card className="sg-frame" data-testid="stack-plan-card">
          <CardHeader><CardTitle>Plan · {plan.target_mood} · {plan.duration_min}min</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="text-xs text-muted-foreground">432Hz tuning · offset {plan.pitch_offset_cents} cents</div>
            <ul className="space-y-2">
              {plan.phases.map((p, idx) => (
                <li key={`phase-${p.carrier_hz}-${p.beat_hz}-${idx}`} className="rounded-md border p-3">
                  <div className="font-medium">{p.title}</div>
                  <div className="text-sm text-muted-foreground">{p.minutes} min · carrier {p.carrier_hz} Hz · beat {p.beat_hz} Hz</div>
                  <div className="text-sm">{p.intention}</div>
                </li>
              ))}
            </ul>
            {(plan.habits || []).length > 0 && (
              <div>
                <div className="font-medium mb-1">Habit stacking</div>
                <ul className="list-disc pl-5 space-y-1 text-sm">
                  {plan.habits.map((h, i) => <li key={`habit-${h.slice(0,20)}-${i}`}>{h}</li>)}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
